import { Component, Inject, OnInit, signal } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DateFilter, Month, Year } from 'src/app/interfaces';
import moment from 'moment';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatLabel, MatFormField, MatSuffix, MatError } from '@angular/material/form-field';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-export-rides',
  templateUrl: './export-rides.component.html',
  styleUrls: ['./export-rides.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatLabel,
    MatRadioGroup,
    MatRadioButton,
    MatFormField,
    MatSelect,
    MatOption,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatCheckbox,
    MatError,
    MatSlideToggle,
    MatDialogActions,
    MatButton,
    MatDialogClose,
  ],
})
export class ExportRidesComponent implements OnInit {
  exportForm: UntypedFormGroup = this.fb.group({
    selectedFilter: ['year'],
    year: [moment().year(), Validators.required],
    month: [moment().month(), Validators.required],
    week: [moment().week(), Validators.required],
    day: [moment().toDate(), Validators.required],
    selectedCharacteristics: [[]],
    exportFromAndTo: [false],
  });
  years = signal<Year[]>([]);
  months: Month[] = [
    { value: 0, viewValue: 'Januari' },
    { value: 1, viewValue: 'Februari' },
    { value: 2, viewValue: 'Maart' },
    { value: 3, viewValue: 'April' },
    { value: 4, viewValue: 'Mei' },
    { value: 5, viewValue: 'Juni' },
    { value: 6, viewValue: 'Juli' },
    { value: 7, viewValue: 'Augustus' },
    { value: 8, viewValue: 'September' },
    { value: 9, viewValue: 'Oktober' },
    { value: 10, viewValue: 'November' },
    { value: 11, viewValue: 'December' },
  ];
  weeks = signal<number[]>([]);
  days = signal<number[]>([]);
  dateFilter: DateFilter[] = [
    { value: 'year', viewValue: 'Jaar' },
    { value: 'month', viewValue: 'Maand' },
    { value: 'week', viewValue: 'Week' },
    { value: 'day', viewValue: 'Dag' },
  ];
  characteristics = this.data.characteristics;
  exporting = signal(false);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<ExportRidesComponent>,
    private snackbar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    const date = new Date();
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();
    const currentWeek = parseInt(moment(date).format('W'));
    const years: Year[] = [];
    let yearInStep = currentYear;
    for (let step = 0; step < 10; step++) {
      years.push({ value: yearInStep, viewValue: yearInStep.toString() });
      yearInStep = yearInStep - 1;
    }
    this.years.set(years);
    this.years().forEach((year) => {
      if (year.value === currentYear) {
        this.exportForm.value.selectedYear = year.value;
      }
    });
    this.months.forEach((month) => {
      if (month.value === currentMonth) {
        this.exportForm.value.selectedMonth = month.value;
      }
    });
    for (let step = 1; step < 54; step++) {
      this.weeks.update((weeks) => [...weeks, step]);
      if (step === currentWeek) {
        this.exportForm.value.selectedWeek = step;
      }
    }
    for (let step = 1; step < 8; step++) {
      this.days.update((days) => [...days, step]);
    }
  }

  periodChange() {
    switch (this.exportForm.controls.selectedFilter.value) {
      case 'year':
        this.exportForm.controls.year.enable();
        this.exportForm.controls.month.disable();
        this.exportForm.controls.day.disable();
        break;
      case 'month':
        this.exportForm.controls.year.enable();
        this.exportForm.controls.month.enable();
        this.exportForm.controls.day.disable();
        break;
      case 'week':
        this.exportForm.controls.year.enable();
        this.exportForm.controls.week.enable();
        this.exportForm.controls.day.disable();
        break;
      case 'day':
        this.exportForm.controls.year.disable();
        this.exportForm.controls.week.disable();
        this.exportForm.controls.day.enable();
        break;
    }
  }

  selectCharacteristic(characteristic) {
    const index = this.exportForm.value.selectedCharacteristics.indexOf(characteristic);
    if (index == -1) {
      this.exportForm.value.selectedCharacteristics.push(characteristic);
    } else {
      this.exportForm.value.selectedCharacteristics.splice(index, 1);
    }
  }

  export() {
    if (this.exporting()) {
      return;
    }
    this.exporting.set(true);
    const exportForm = this.exportForm.value;
    if (!exportForm.selectedFilter) {
      return this.snackbar.open('Selecteer een filter om verder te gaan.', 'X', {
        duration: 5000,
      });
    }
    if (exportForm.selectedFilter == 'day' && exportForm.day == null) {
      return this.snackbar.open('Kies een datum om verder te gaan.', 'X', {
        duration: 5000,
      });
    }
    let valueToExport = {};
    valueToExport['type'] = exportForm.selectedFilter;
    valueToExport['exportFromAndTo'] = exportForm.exportFromAndTo;
    switch (exportForm.selectedFilter) {
      case 'day':
        valueToExport['day'] = exportForm.day.getDate();
        valueToExport['month'] = exportForm.day.getMonth();
        valueToExport['year'] = exportForm.day.getFullYear();
        break;
      case 'week':
        valueToExport['week'] = `${exportForm.year}-${exportForm.week}`;
        break;
      case 'month':
        valueToExport['month'] = exportForm.month;
        valueToExport['year'] = exportForm.year;
        break;
      case 'year':
        valueToExport['year'] = exportForm.year;
        break;
    }
    valueToExport['characteristics'] = exportForm.selectedCharacteristics;

    if (!valueToExport) {
      this.exporting.set(false);
      return this.snackbar.open('Vul een waarde in om verder te gaan.', 'X', {
        duration: 5000,
      });
    }
    this.dialogRef.close(valueToExport);
  }
}
